import { useEffect, useState } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { disconnect, getAccessToken, onDisconnected, YoutubeAccountError } from "./youtubeAccount.js";
import { topicNames, type SubscriptionChannel } from "./socialRows.js";

/**
 * A profile's own YouTube subscriptions (build guide docs/social-connect-youtube.md, Phase 2c).
 *
 * Read from the device straight to Google with the profile's access token (the refresh token
 * never leaves the APK). Our server is never told who the viewer is: it later receives only the
 * channel ids, to answer "latest video and who is live".
 *
 * Quota: subscriptions.list is 1 unit a page (at most 4 pages = 200 channels), channels.list
 * 1 unit per 50 channels. The answer is cached per profile and refreshed at most once a day.
 * YouTube's data-retention rule: nothing is kept past 30 days, and a disconnect deletes it.
 */

const API = "https://www.googleapis.com/youtube/v3";
export const MAX_PAGES = 4;
export const REFRESH_AFTER_MS = 24 * 60 * 60 * 1000;
export const KEEP_FOR_MS = 30 * 24 * 60 * 60 * 1000;

export interface SubscriptionsEntry {
  refreshedAt: number;
  /** The signed-in account's channel name, when it has a channel. */
  accountName?: string;
  channels: SubscriptionChannel[];
}

interface SubscriptionStore {
  byProfile: Record<string, SubscriptionsEntry>;
  put: (profileId: string, entry: SubscriptionsEntry) => void;
  clear: (profileId: string) => void;
  /** Deletes anything older than YouTube's 30 days. */
  prune: (now: number) => void;
}

/**
 * Its own key: "slique-social" still holds each profile's starter-creator choice (socialStore.ts),
 * which a profile that has not connected YouTube keeps using.
 */
export const useSubscriptionStore = create<SubscriptionStore>()(
  persist(
    (set) => ({
      byProfile: {},
      put: (profileId, entry) => set((state) => ({ byProfile: { ...state.byProfile, [profileId]: entry } })),
      clear: (profileId) =>
        set((state) => {
          if (!(profileId in state.byProfile)) return state;
          const { [profileId]: _gone, ...rest } = state.byProfile;
          return { byProfile: rest };
        }),
      prune: (now) =>
        set((state) => {
          const kept = Object.fromEntries(Object.entries(state.byProfile).filter(([, entry]) => now - entry.refreshedAt < KEEP_FOR_MS));
          return Object.keys(kept).length === Object.keys(state.byProfile).length ? state : { byProfile: kept };
        }),
    }),
    {
      name: "slique-subscriptions",
      version: 1,
      partialize: (state) => ({ byProfile: state.byProfile }),
    },
  ),
);

// A disconnected profile's cached subscriptions are deleted, not kept.
onDisconnected((profileId) => useSubscriptionStore.getState().clear(profileId));

// --- Google's answers ------------------------------------------------------------------------

interface SubscriptionsPage {
  channels: Array<{ id: string; title: string; thumb?: string }>;
  nextPageToken?: string;
}

export function parseSubscriptionsPage(json: unknown): SubscriptionsPage {
  const root = (json ?? {}) as { items?: unknown; nextPageToken?: unknown };
  const channels: SubscriptionsPage["channels"] = [];
  for (const item of Array.isArray(root.items) ? root.items : []) {
    const snippet = (item as { snippet?: { title?: unknown; resourceId?: { channelId?: unknown }; thumbnails?: { default?: { url?: unknown } } } }).snippet;
    const id = snippet?.resourceId?.channelId;
    const title = snippet?.title;
    if (typeof id !== "string" || !id || typeof title !== "string") continue;
    const thumb = snippet?.thumbnails?.default?.url;
    channels.push({ id, title, ...(typeof thumb === "string" ? { thumb } : {}) });
  }
  return { channels, ...(typeof root.nextPageToken === "string" && root.nextPageToken ? { nextPageToken: root.nextPageToken } : {}) };
}

/** channel id -> its topic names. */
export function parseTopics(json: unknown): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const item of Array.isArray((json as { items?: unknown })?.items) ? ((json as { items: unknown[] }).items) : []) {
    const entry = item as { id?: unknown; topicDetails?: { topicCategories?: unknown } };
    if (typeof entry.id !== "string") continue;
    const categories = Array.isArray(entry.topicDetails?.topicCategories) ? (entry.topicDetails!.topicCategories as unknown[]).filter((c): c is string => typeof c === "string") : [];
    out.set(entry.id, topicNames(categories));
  }
  return out;
}

export function parseAccountName(json: unknown): string | undefined {
  const items = (json as { items?: unknown })?.items;
  const title = Array.isArray(items) ? (items[0] as { snippet?: { title?: unknown } } | undefined)?.snippet?.title : undefined;
  return typeof title === "string" && title.trim() ? title.trim() : undefined;
}

async function get(path: string, accessToken: string): Promise<unknown> {
  const response = await fetch(`${API}/${path}`, { headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/json" } });
  if (!response.ok) throw new Error(`YouTube ${response.status}`);
  return response.json();
}

/** Fetches the profile's subscriptions with their topics; throws on any failure (the caller keeps the old cache). */
export async function fetchSubscriptions(accessToken: string): Promise<{ channels: SubscriptionChannel[]; accountName?: string }> {
  const found: SubscriptionsPage["channels"] = [];
  let pageToken: string | undefined;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const query = `subscriptions?part=snippet&mine=true&maxResults=50&order=relevance${pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : ""}`;
    const parsed = parseSubscriptionsPage(await get(query, accessToken));
    found.push(...parsed.channels);
    pageToken = parsed.nextPageToken;
    if (!pageToken) break;
  }
  const seen = new Set<string>();
  const unique = found.filter((channel) => (seen.has(channel.id) ? false : (seen.add(channel.id), true)));

  const topics = new Map<string, string[]>();
  for (let i = 0; i < unique.length; i += 50) {
    const ids = unique.slice(i, i + 50).map((c) => c.id).join(",");
    // Topics are a nicety: a failed batch leaves those channels in "Everything else".
    try {
      for (const [id, names] of parseTopics(await get(`channels?part=topicDetails&id=${ids}&maxResults=50`, accessToken))) topics.set(id, names);
    } catch {
      /* keep going */
    }
  }

  let accountName: string | undefined;
  try {
    accountName = parseAccountName(await get("channels?part=snippet&mine=true", accessToken));
  } catch {
    /* an account without a channel has no name to show */
  }
  return {
    channels: unique.map((channel) => ({ ...channel, topics: topics.get(channel.id) ?? [] })),
    ...(accountName ? { accountName } : {}),
  };
}

export type SubscriptionsStatus = "loading" | "ready" | "error";

/**
 * The profile's subscriptions: the cache at once, refreshed when older than a day. `disconnected`
 * from the plugin (the viewer removed access at Google) clears the cache and disconnects, so the
 * page falls back to the Connect panel. Switching profiles answers for the other profile at once.
 */
export function useSubscriptions(profileId: string | undefined, enabled: boolean): {
  channels: SubscriptionChannel[] | null;
  accountName?: string;
  status: SubscriptionsStatus;
} {
  const entry = useSubscriptionStore((state) => (profileId ? state.byProfile[profileId] : undefined));
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !profileId) return;
    const store = useSubscriptionStore.getState();
    store.prune(Date.now());
    const held = useSubscriptionStore.getState().byProfile[profileId];
    if (held && Date.now() - held.refreshedAt < REFRESH_AFTER_MS) return;
    let live = true;
    setFailed(null);
    void (async () => {
      try {
        const token = await getAccessToken(profileId);
        const fresh = await fetchSubscriptions(token.accessToken);
        useSubscriptionStore.getState().put(profileId, { refreshedAt: Date.now(), channels: fresh.channels, ...(fresh.accountName ? { accountName: fresh.accountName } : {}) });
      } catch (error) {
        if (error instanceof YoutubeAccountError && error.code === "disconnected") {
          await disconnect(profileId);
        } else if (live) {
          setFailed(profileId);
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [profileId, enabled]);

  if (entry) return { channels: entry.channels, accountName: entry.accountName, status: "ready" };
  return { channels: null, status: failed === profileId ? "error" : "loading" };
}
